import { mix } from '../icons/pix';
import { INK, Layer, type Size } from './grid';

/**
 * The plate every move icon sits on. Its SHAPE and RIM COLOUR say the move's kind (readable even
 * without colour), its GROUND tint says the element, and a faint watermark says the school.
 *
 *   attack (攻) round, vermilion rim        break (破) cut-corner, amber rim
 *   power  (怒) round + 4 studs, gold rim   heal   (療) diamond, jade rim
 *   buff   (氣) hexagon, sky-blue rim
 *
 * Shapes are defined on the 24 grid (centre 12,12, 2 px margin) and sampled at any size.
 */
export type MoveKind = 'attack' | 'break' | 'power' | 'heal' | 'buff';

export const KIND_RIM: Readonly<Record<MoveKind, string>> = {
  attack: '#e24a34',
  break: '#f59a2c',
  power: '#f6cf4a',
  heal: '#55cf72',
  buff: '#4fb0f0',
};

type Inside = (dx: number, dy: number) => boolean;

const SHAPES_ROUND: Readonly<Record<MoveKind, Inside>> = {
  attack: (dx, dy) => dx * dx + dy * dy <= 10.1 * 10.1,
  break: (dx, dy) => Math.abs(dx) <= 10 && Math.abs(dy) <= 10 && Math.abs(dx) + Math.abs(dy) <= 14.5,
  power: (dx, dy) =>
    dx * dx + dy * dy <= 9.1 * 9.1 ||
    (Math.abs(dx) <= 1.6 && Math.abs(dy) <= 10) ||
    (Math.abs(dy) <= 1.6 && Math.abs(dx) <= 10),
  heal: (dx, dy) => Math.abs(dx) + Math.abs(dy) <= 12.6 && Math.abs(dx) <= 10 && Math.abs(dy) <= 10,
  buff: (dx, dy) => Math.abs(dy) <= 9.6 && Math.abs(dx) + Math.abs(dy) * 0.5 <= 10.2,
};

/** Square plates (classic MMO skill frame): the kind reads from the cut corners, the rim colour and a corner gem. */
const corner = (dx: number, dy: number, r: number): boolean => {
  const ax = Math.abs(dx) - (11 - r);
  const ay = Math.abs(dy) - (11 - r);
  return ax > 0 && ay > 0 && ax * ax + ay * ay > r * r;
};
const SQUARES: Readonly<Record<MoveKind, Inside>> = {
  attack: (dx, dy) => Math.abs(dx) <= 11 && Math.abs(dy) <= 11 && !corner(dx, dy, 1.5),
  break: (dx, dy) => Math.abs(dx) <= 11 && Math.abs(dy) <= 11 && dx - dy <= 17 && dy - dx <= 17,
  power: (dx, dy) => Math.abs(dx) <= 11 && Math.abs(dy) <= 11 && !corner(dx, dy, 1),
  heal: (dx, dy) => Math.abs(dx) <= 11 && Math.abs(dy) <= 11 && !corner(dx, dy, 5),
  buff: (dx, dy) => Math.abs(dx) <= 11 && Math.abs(dy) <= 11 && Math.abs(dx) + (dy < 0 ? -dy : 0) <= 16,
};
export type Shape = 'round' | 'square';

export function plateMask(kind: MoveKind, n: number, shape: Shape = 'round'): boolean[] {
  const k = n / 24;
  const SHAPES = shape === 'square' ? SQUARES : SHAPES_ROUND;
  const m = new Array<boolean>(n * n).fill(false);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) m[y * n + x] = SHAPES[kind]((x + 0.5) / k - 12, (y + 0.5) / k - 12);
  return m;
}

const erode = (m: boolean[], n: number): boolean[] => {
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < n && y < n && m[y * n + x]!;
  return m.map((v, i) => v && at((i % n) - 1, (i / n) | 0) && at((i % n) + 1, (i / n) | 0) && at(i % n, ((i / n) | 0) - 1) && at(i % n, ((i / n) | 0) + 1));
};

/** Draw the plate; returns the layer and the inner mask (where the school watermark may go). */
export function drawPlate(kind: MoveKind, ground: string, n: Size, shape: Shape = 'round'): { layer: Layer; inner: boolean[] } {
  const L = new Layer(n);
  const rim = KIND_RIM[kind];
  const rimW = (n <= 16 ? 1 : n <= 32 ? 2 : 3) + (shape === 'square' && kind === 'power' ? 1 : 0);
  const bands: boolean[][] = [plateMask(kind, n, shape)];
  for (let i = 0; i < rimW + 1; i++) bands.push(erode(bands[i]!, n));
  const depth = (i: number): number => {
    let d = 0;
    while (d < bands.length && bands[d]![i]) d++;
    return d; // 0 outside, 1 outline ring, 2.. rim bands, then ground
  };
  const inner = bands[rimW + 1]!;
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const i = y * n + x;
      const d = depth(i);
      if (d === 0) continue;
      const u = (x - y) / (n * 1.15);
      const lit = u > 0.04;
      const dark = u < -0.1;
      if (d === 1) L.d[i] = mix(rim, INK, 0.78);
      else if (d <= rimW + 1) {
        // outer rim bands bright, the last one a shaded bevel
        const last = d === rimW + 1 && n > 16;
        if (last) L.d[i] = lit ? mix(rim, ground, 0.35) : mix(rim, INK, 0.45);
        else L.d[i] = lit ? mix(rim, '#ffffff', 0.3) : dark ? mix(rim, INK, 0.2) : rim;
      } else L.d[i] = mix(ground, '#ffffff', Math.max(0, u) * 0.16 + 0.02);
    }
  // power: a maroon core on each gold stud so it reads as a crown
  if (kind === 'power' && n >= 24 && shape === 'round') {
    const k = n / 24;
    for (const [sx, sy] of [[12, 2], [12, 21], [2, 12], [21, 12]] as const) L.rect(sx, sy, 1, 1, '#7a1520');
    void k;
  }
  // 48: a soft gloss arc on the ground, top right
  if (n >= 48) {
    for (let y = 0; y < n; y++)
      for (let x = 0; x < n; x++) {
        const i = y * n + x;
        if (!inner[i]) continue;
        const dx = x - n * 0.62;
        const dy = y - n * 0.3;
        if (dx * dx + dy * dy < (n * 0.22) ** 2 && L.d[i]) L.d[i] = mix(L.d[i]!, '#ffffff', 0.07);
      }
  }
  return { layer: L, inner };
}

type School = string;

/** A faint school sign behind the picture, drawn in 24-grid coordinates. */
export function schoolMark(school: School, c: string, n: Size): Layer {
  const L = new Layer(n);
  switch (school) {
    case 'ยอดหมอก': // mist peaks
      L.poly([[5, 16], [9, 8], [12, 13], [15, 6], [19, 16]], c);
      L.line(4, 18, 19, 18, c);
      L.line(6, 20, 17, 20, c);
      break;
    case 'พเนจร': // wanderer's hat and road
      L.arc(11.5, 15, 7, Math.PI, 2 * Math.PI, c);
      L.line(3, 15, 20, 15, c);
      L.line(8, 18, 15, 18, c);
      break;
    case 'หินระฆัง': // bell
      L.arc(11.5, 11, 6, Math.PI, 2 * Math.PI, c);
      L.line(5, 11, 5, 16, c);
      L.line(18, 11, 18, 16, c);
      L.line(3, 17, 20, 17, c);
      L.line(11, 4, 11, 5, c);
      break;
    case 'จันทร์มัว': // dim moon
      L.arc(11.5, 11.5, 7, Math.PI * 0.3, Math.PI * 1.7, c);
      L.arc(14, 11.5, 6, Math.PI * 0.35, Math.PI * 1.65, c);
      break;
    case 'หอหมึกสน': // pine
      L.poly([[11, 3], [6, 9], [9, 9], [5, 14], [10, 14], [11, 14]], c);
      L.poly([[11, 3], [16, 9], [13, 9], [18, 14], [13, 14]], c);
      L.line(11, 14, 11, 20, c);
      break;
    case 'ดาบชาด': // blood drop
      L.poly([[11, 3], [6, 11]], c);
      L.poly([[11, 3], [17, 11]], c);
      L.arc(11.5, 13, 6, Math.PI * 0.05, Math.PI * 0.95, c);
      break;
    case 'ทางโคมเหล็ก': // lantern
      L.rect(7, 6, 10, 12, c);
      L.line(5, 5, 18, 5, c);
      L.line(5, 19, 18, 19, c);
      L.line(11, 2, 11, 4, c);
      L.line(7, 12, 16, 12, c);
      break;
    case 'ศิลาเก้ากระบี่': // three standing swords
      for (const x of [7, 11, 16]) {
        L.line(x, 5, x, 16, c);
        L.line(x - 2, 17, x + 2, 17, c);
      }
      break;
    case 'หุบเขาเมฆา': // cloud
      L.arc(8, 14, 3, Math.PI, 2 * Math.PI, c);
      L.arc(12, 11, 4, Math.PI, 2 * Math.PI, c);
      L.arc(16, 14, 3, Math.PI * 1.1, 2 * Math.PI + 0.2, c);
      L.line(5, 17, 19, 17, c);
      break;
    default:
      break;
  }
  return L;
}
